import { getConditionalFormatColor } from "./resultGridHelper"

const categoricalChartPalette = [
	"#2563EB",
	"#DC2626",
	"#059669",
	"#D97706",
	"#7C3AED",
	"#0F766E",
	"#DB2777",
	"#4F46E5",
	"#65A30D",
	"#C2410C",
	"#0891B2",
	"#9333EA",
	"#EA580C",
	"#16A34A",
	"#0284C7",
	"#BE123C",
	"#CA8A04",
	"#7E22CE",
	"#0D9488",
	"#1D4ED8",
]

/**
 * Generates an array of colors for chart bars based on values using the same
 * logic as the AG Grid conditional formatting. Green for low values, red for high values.
 * 
 * @param values - Array of numeric values to generate colors for
 * @returns Array of color strings (hex format) for each value
 */
export function generateChartColors(values: number[]): string[] {
  // Filter out null/undefined/NaN values for min/max calculation
  const validValues = values.filter(value => value !== null && value !== undefined && !isNaN(value))
  
  // If no valid values, return array of default blue colors
  if (validValues.length === 0) {
    return values.map(() => "#5070CD")
  }
  
  // Calculate min and max from the valid values
  const minValue = Math.min(...validValues)
  const maxValue = Math.max(...validValues)
  
  // Generate colors using the same logic as AG Grid
  return values.map(value => {
    if (value === null || value === undefined || isNaN(value)) {
      return "#5070CD" // Default blue for invalid values
    }
    
    return getConditionalFormatColor(value, minValue, maxValue)
  })
}

/**
 * Generates chart colors for a specific field from an array of objects
 * 
 * @param data - Array of objects containing the data
 * @param fieldName - Name of the field to extract values from
 * @returns Array of color strings (hex format) for each value
 */
export function generateChartColorsFromField<T>(data: T[], fieldName: keyof T): string[] {
  const values = data.map(item => {
    const value = item[fieldName]
    return typeof value === 'number' ? value : 0
  })
  
  return generateChartColors(values)
}

/**
 * Generates a distinct categorical palette for label-based charts such as pie charts.
 * Colors are assigned by label order rather than value magnitude so adjacent slices remain distinguishable.
 *
 * @param labels - Array of labels to generate categorical colors for
 * @returns Array of color strings for each label
 */
export function generateCategoricalChartColors(labels: string[]): string[] {
	return labels.map((_, index) => categoricalChartPalette[index % categoricalChartPalette.length])
}
